import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X, Settings2, Trash2 } from "lucide-react";
import { listStudents, enrollStudent, updateStudent, addStudentContent, listStudentContent, deleteStudentContent } from "../adminApi";

const CONTENT_TYPES = ["material", "class", "recording", "assignment", "attendance", "certificate"];
const FIELDS = {
  material: [["title", "Title"], ["link", "Link"], ["description", "Description"]],
  class: [["title", "Title"], ["link", "Join link"], ["datetime", "Date & time"]],
  recording: [["title", "Title"], ["link", "Recording link"]],
  assignment: [["title", "Title"], ["description", "Description"], ["dueDate", "Due date"]],
  attendance: [["date", "Date"], ["status", "Status (Present/Absent)"]],
  certificate: [["title", "Title"], ["link", "Certificate link"], ["issuedDate", "Issued date"]],
};

export default function AdminStudents() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [enroll, setEnroll] = useState(false);
  const [manage, setManage] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setData(await listStudents({ search: search || undefined, limit: 50 })); }
    catch { toast.error("Failed to load students."); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const assign = async (s, field, value) => {
    try { await updateStudent(s.id, { [field]: field === "progress" ? Number(value) : value }); toast.success("Updated."); load(); }
    catch { toast.error("Update failed."); }
  };

  return (
    <div data-testid="admin-students">
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">Students</h1><p className="text-sm text-slate-500">{data.total} enrolled student(s)</p></div>
        <button onClick={() => setEnroll(true)} data-testid="enroll-student" className="inline-flex items-center gap-2 rounded-lg bg-orange px-4 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> Enroll Student</button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mb-4 flex gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search students…" data-testid="students-search" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange" />
        <button className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">Search</button>
      </form>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div> : data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">No students yet.</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Program</th><th className="px-4 py-3">Progress</th><th className="px-4 py-3 text-right">Manage</th></tr></thead>
            <tbody>{data.items.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid="student-row">
                <td className="px-4 py-3 font-medium text-slate-800">{s.name}</td>
                <td className="px-4 py-3 text-slate-600">{s.email}</td>
                <td className="px-4 py-3"><input defaultValue={s.enrolledProgram} onBlur={(e) => e.target.value !== s.enrolledProgram && assign(s, "enrolledProgram", e.target.value)} data-testid="assign-program" placeholder="Assign program" className="w-44 rounded border border-slate-300 px-2 py-1 text-xs outline-none focus:border-orange" /></td>
                <td className="px-4 py-3"><input type="number" defaultValue={s.progress} onBlur={(e) => Number(e.target.value) !== s.progress && assign(s, "progress", e.target.value)} className="w-16 rounded border border-slate-300 px-2 py-1 text-xs outline-none focus:border-orange" />%</td>
                <td className="px-4 py-3 text-right"><button onClick={() => setManage(s)} data-testid="manage-student" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"><Settings2 className="h-3.5 w-3.5" /> Content</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {enroll && <EnrollModal onClose={() => setEnroll(false)} onDone={() => { setEnroll(false); load(); }} />}
      {manage && <ContentModal student={manage} onClose={() => setManage(null)} />}
    </div>
  );
}

function EnrollModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", mobile: "", enrolledProgram: "", college: "", city: "" });
  const [saving, setSaving] = useState(false);
  const [temp, setTemp] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async () => {
    if (!f.name || !f.email || !f.mobile) return toast.error("Name, email and mobile are required.");
    setSaving(true);
    try { const r = await enrollStudent(f); setTemp(r.tempPassword); toast.success("Student enrolled."); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="enroll-modal">
        <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-bold text-slate-900">Enroll Student</h2><button onClick={onClose}><X className="h-5 w-5 text-slate-500" /></button></div>
        {temp ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-600">Student created. Share these login credentials:</p>
            <div className="rounded-lg bg-slate-50 p-4 text-sm"><div><b>Email:</b> {f.email}</div><div><b>Temp password:</b> <span data-testid="temp-password">{temp}</span></div></div>
            <button onClick={onDone} className="rounded-lg bg-orange py-2.5 font-medium text-white">Done</button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {[["name", "Full name"], ["email", "Email"], ["mobile", "Mobile"], ["enrolledProgram", "Program (optional)"], ["college", "College (optional)"], ["city", "City (optional)"]].map(([k, l]) => (
              <input key={k} value={f[k]} onChange={set(k)} placeholder={l} data-testid={`enroll-${k}`} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange" />
            ))}
            <button onClick={submit} disabled={saving} data-testid="enroll-submit" className="rounded-lg bg-orange py-2.5 font-medium text-white disabled:opacity-60">{saving ? "Enrolling…" : "Enroll"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ContentModal({ student, onClose }) {
  const [type, setType] = useState("material");
  const [f, setF] = useState({});
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = async () => { try { setItems(await listStudentContent(student.id)); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const add = async () => {
    setSaving(true);
    try { await addStudentContent({ studentId: student.id, type, ...f }); toast.success("Added."); setF({}); load(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await deleteStudentContent(id); load(); } catch { toast.error("Delete failed."); } };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="content-modal">
        <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-bold text-slate-900">Content · {student.name}</h2><button onClick={onClose}><X className="h-5 w-5 text-slate-500" /></button></div>
        <select value={type} onChange={(e) => { setType(e.target.value); setF({}); }} data-testid="content-type" className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm capitalize outline-none focus:border-orange">
          {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="mb-3 flex flex-col gap-2">
          {FIELDS[type].map(([k, l]) => <input key={k} value={f[k] || ""} onChange={set(k)} placeholder={l} data-testid={`content-${k}`} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange" />)}
        </div>
        <button onClick={add} disabled={saving} data-testid="content-add" className="mb-5 w-full rounded-lg bg-orange py-2 text-sm font-medium text-white disabled:opacity-60">{saving ? "Adding…" : "Add content"}</button>
        <div className="space-y-2">
          {items.length === 0 ? <p className="text-center text-xs text-slate-400">No content assigned yet.</p> : items.map((it) => (
            <div key={it.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <span className="text-slate-700"><b className="capitalize text-slate-400">{it.type}</b> · {it.title || it.date || it.link}</span>
              <button onClick={() => del(it.id)} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
